// Lifted Views News — public index.
//
// Shows the latest published articles filtered by category and state.
// Empty state is HONEST: when no articles exist we tell the visitor the
// desk is spinning up — we never render fake headlines (per owner directive).
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Newspaper, ChevronRight, Clock, MapPin, Tag, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import { ShareBar } from "@/components/news/ArticleExtras";
import PageMeta from "@/components/PageMeta";

const CATEGORY_LABELS = {
  cannabis: "Cannabis",
  hemp_cbd: "Hemp & CBD",
  vapes_carts: "Vapes & Carts",
  nicotine_disposables: "Nicotine & Disposables",
  state_policy: "State Policy & Bans",
  business: "Business",
  culture: "Culture",
};

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC",
];

export default function LiftedViewsNews() {
  const [articles, setArticles] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");
  const [state, setState] = useState("");

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category) params.set("category", category);
      if (state) params.set("state", state);
      const { data } = await api.get(`/news/articles?${params.toString()}`);
      setArticles(data.articles || []);
      setCounts(data.counts || {});
    } catch {
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category, state]);

  const featured = articles.find((a) => a.featured) || articles[0];
  const rest = articles.filter((a) => a.slug !== featured?.slug);

  return (
    <div className="min-h-screen bg-[#04070a] text-white" data-testid="news-index-page">
      <PageMeta
        title="Lifted Views News — Cannabis, CBD, Vapes, State policy"
        description="News, policy, and culture from the Lifted desk. Verified sources, source-grounded summaries."
        path="/lifted-views-news"
      />

      {/* HERO */}
      <section className="border-b border-white/5">
        <div className="w-full bg-black overflow-hidden">
          <img src="/images/covers/lifted-views-news.webp" alt="Lifted Views News cover — cannabis leaf, Earth from space, and a Lifted News newspaper"
            className="w-full max-w-[1200px] mx-auto block h-auto aspect-[16/9] object-contain" fetchpriority="high" data-testid="news-cover-image" />
        </div>
        <div className="container mx-auto px-4 py-8 lg:py-10">
          <div className="eyebrow text-lifted-green flex items-center gap-2 mb-3">
            <Newspaper className="h-3 w-3" /> The Desk
          </div>
          <h1 className="sr-only">Lifted Views News</h1>
          <p className="text-white/70 max-w-2xl text-lg">
            Cannabis. Hemp & CBD. Vapes. State policy. Business. Culture.
            Source-grounded coverage from the Lifted desk.
          </p>
        </div>
      </section>

      {/* FILTERS */}
      <section className="border-b border-white/5 bg-black/30" data-testid="news-filters">
        <div className="container mx-auto px-4 py-5 flex flex-wrap gap-2 items-center">
          <button
            onClick={() => setCategory("")}
            data-testid="news-cat-all"
            className={`text-[10px] font-bebas uppercase tracking-[0.2em] px-3 py-2 rounded border transition-colors ${
              !category ? "bg-lifted-green text-black border-lifted-green" : "border-white/20 text-white/70 hover:border-lifted-green"
            }`}>
            All ({articles.length})
          </button>
          {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setCategory(category === key ? "" : key)}
              data-testid={`news-cat-${key}`}
              className={`text-[10px] font-bebas uppercase tracking-[0.2em] px-3 py-2 rounded border transition-colors ${
                category === key ? "bg-lifted-green text-black border-lifted-green" : "border-white/20 text-white/70 hover:border-lifted-green"
              }`}>
              {label} ({counts[key] || 0})
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <MapPin className="h-4 w-4 text-lifted-green" />
            <select value={state} onChange={(e) => setState(e.target.value)}
              className="input-lifted text-xs" data-testid="news-state-filter">
              <option value="">All states</option>
              {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
      </section>

      {/* RESULTS */}
      <section className="py-10 lg:py-14">
        <div className="container mx-auto px-4">
          {loading && (
            <p className="text-center text-white/50" data-testid="news-loading">Loading…</p>
          )}
          {!loading && articles.length === 0 && (
            <div className="max-w-xl mx-auto text-center py-12" data-testid="news-empty">
              <AlertCircle className="h-10 w-10 text-lifted-green mx-auto mb-4" />
              <h2 className="font-anton uppercase text-2xl text-white">
                The Desk Is <span className="italic text-lifted-green">Warming Up</span>
              </h2>
              <p className="text-white/60 text-sm mt-3">
                No articles are published yet for the selected filters. The Lifted news desk is being
                set up with verified sources — check back shortly, or follow us for the first drop.
              </p>
            </div>
          )}
          {!loading && featured && (
            <article className="glass-panel p-6 lg:p-8 mb-8" data-testid="news-featured">
              <div className="eyebrow text-lifted-green mb-2">Featured</div>
              <h2 className="font-anton uppercase text-3xl lg:text-4xl text-white">
                <Link to={`/lifted-views-news/${featured.slug}`} className="hover:text-lifted-green">
                  {featured.headline}
                </Link>
              </h2>
              <p className="text-white/70 mt-3 text-sm">{featured.summary}</p>
              <ArticleMeta a={featured} />
              <ShareBar article={featured} compact />
            </article>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {rest.map((a) => (
              <div key={a.slug} data-testid={`news-card-${a.slug}`} className="glass-panel p-5 hover:border-lifted-green/40 border border-transparent transition-[transform,border-color] hover:-translate-y-0.5">
              <Link to={`/lifted-views-news/${a.slug}`} data-testid={`news-item-${a.slug}`} className="block">
                {a.image?.thumb_url || a.image?.url ? (
                  <img src={a.image.thumb_url || a.image.url} alt={a.image.alt} loading="lazy" className="w-full aspect-[16/9] object-cover rounded mb-3" />
                ) : null}
                <div className="flex items-center gap-2 text-[10px] text-lifted-green font-bebas uppercase tracking-[0.2em]">
                  <Tag className="h-3 w-3" /> {CATEGORY_LABELS[a.category] || a.category}
                  {a.state && <><span>·</span> <MapPin className="h-3 w-3" /> {a.state}</>}
                </div>
                <h3 className="font-anton uppercase text-xl text-white mt-3 leading-tight">{a.headline}</h3>
                <p className="text-xs text-white/60 mt-2 line-clamp-3">{a.summary}</p>
                <ArticleMeta a={a} compact />
              </Link>
              <ShareBar article={a} compact />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export function ArticleMeta({ a, compact = false }) {
  const date = a.published_at ? new Date(a.published_at).toLocaleDateString() : "";
  return (
    <div className={`flex flex-wrap gap-3 text-[10px] text-white/50 mt-${compact ? "3" : "4"}`}>
      {date && <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {date}</span>}
      {a.source_publisher && <span>Source: {a.source_publisher}</span>}
      {a.policy_status && (
        <span className="text-lifted-green font-bebas uppercase tracking-wider">
          Policy: {a.policy_status}
        </span>
      )}
      {a.ai_assisted && <span className="text-white/50">AI-assisted</span>}
    </div>
  );
}
